import { useForm } from "react-hook-form";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { LeadFormData } from "./types";
import {
  cruiseSuiteInfoToLeadFormData,
  formDataToLeadAPIRequest
} from "./utils";
import React, { startTransition, useCallback, useEffect } from "react";
import {
  FormRequestType,
  LeadAPIInvalidEmailError,
  LeadAPIRequest,
  sendLead
} from "src/ssc-api/leadAPIClient";
import {
  getVisitorAuid,
  getMarketingCloudVisitorID,
  trackFormSubmitted
} from "src/utils/analytics/tracking";
import { useLingui } from "@lingui/react/macro";
import { isPresent } from "src/utils/checks";
import { LeadApiResponse } from "@silversea-ssc/dotcom-api-types/Lead";

export type CruiseSuiteFormProps = {
  voyageName?: string;
  voyageCode?: string;
  departureDate?: string;
  voyageLength?: string;
  shipName?: string;
  price?: number;
  suiteVariation?: string;
};

type UseLeadFormOptions = {
  email?: string;
  onSubmitSuccess: (
    formData: LeadFormData,
    requestData: LeadAPIRequest,
    responseData: LeadApiResponse
  ) => void;
  requestSource: string;
  requestType: FormRequestType;
  brochureCode?: string;
  askImNotTraveAgent: boolean;
  askWorkWithAgent: boolean;
  enableSubscribeEmail?: boolean;
  cruiseSuiteInfo?: CruiseSuiteFormProps;
  formName: string;
};

export const useLeadForm = ({
  requestSource,
  requestType,
  askWorkWithAgent,
  brochureCode,
  enableSubscribeEmail,
  askImNotTraveAgent,
  email,
  cruiseSuiteInfo,
  onSubmitSuccess,
  formName
}: UseLeadFormOptions) => {
  const language = useLanguage();
  const geolocation = useGeolocation();
  const { i18n, t } = useLingui();

  const invalidEmailMessage = i18n._(t`form-invalid-email-address`);

  const defaultValues = React.useMemo(() => {
    const data: LeadFormData = {
      RequestSource: requestSource,
      RequestType: requestType,
      SiteCountry: geolocation?.countryCodeISO3 || "",
      SiteLanguage: language.code.toUpperCase(),
      SiteCurrency: geolocation.defaultCurrency,

      WorkingWithAgent: askWorkWithAgent ? false : undefined,
      BrochuresRequested: brochureCode ? brochureCode : undefined,
      SubscribeEmail: enableSubscribeEmail ? "true" : undefined,
      IsNotAgent: askImNotTraveAgent ? true : undefined,
      Email: email && email.trim() != "" ? email : undefined,
      //Country: geolocation?.countryCodeISO3 || "",
      ...cruiseSuiteInfoToLeadFormData(cruiseSuiteInfo)
    };
    return data;
  }, [
    askImNotTraveAgent,
    askWorkWithAgent,
    brochureCode,
    cruiseSuiteInfo,
    email,
    enableSubscribeEmail,
    geolocation?.countryCodeISO3,
    geolocation.defaultCurrency,
    language.code,
    requestSource,
    requestType
  ]);

  const formMethods = useForm<LeadFormData>({
    mode: "onTouched",
    reValidateMode: "onChange",
    shouldUnregister: true,
    defaultValues: defaultValues,
    shouldFocusError: true,
    criteriaMode: "all"
  });
  const { setValue, setError, formState } = formMethods;

  useEffect(() => {
    startTransition(() => {
      //ensure form has updated version of defaultValues (ie cruise/suite data)
      Object.keys(defaultValues).forEach((key) => {
        setValue(key as keyof LeadFormData, defaultValues[key], {
          shouldDirty: false,
          shouldValidate: false
        });
      });
    });
  }, [defaultValues, setValue]);

  const onSubmit = useCallback(
    async (data: LeadFormData) => {
      try {
        const isValid = formState.isValid;
        if (!isValid) {
          return false;
        }

        const visitorAuid = getVisitorAuid();
        const marketingCloudVisitorID = getMarketingCloudVisitorID();

        const patchedData = {
          ...data,
          MCId: marketingCloudVisitorID,
          AUID: visitorAuid
        };
        const request = formDataToLeadAPIRequest(patchedData);
        const isPhoneSubmitted =
          isPresent(request.Phone) && request.Phone != "";

        const leadApiResponse = await sendLead(request);
        await trackFormSubmitted(
          {
            ...leadApiResponse,
            formName,
            phone_number_is_present: isPhoneSubmitted
          },
          data
        );
        onSubmitSuccess(patchedData, request, leadApiResponse);

        return true;
      } catch (e) {
        console.error("request", e);
        if (e instanceof LeadAPIInvalidEmailError) {
          setError("Email", {
            type: "invalid",
            message: invalidEmailMessage
          });
        }
      }
      return false;
    },
    [
      invalidEmailMessage,
      formName,
      formState.isValid,
      onSubmitSuccess,
      setError
    ]
  );

  return {
    formMethods,
    onSubmit
  };
};
